/**
 * MyCraftzStagePage — /mycraftz/stage/:stage  (Pages 5–9 of the master mockup)
 *
 * ONE shared page that renders the appropriate layout per stage key.
 *  • jigyasa  → Page 5 (Book Selected / Powerful Idea / Why This Matters)
 *  • bodha    → Page 6 (Key Insights numbered list)
 *  • sankalpa → Page 7 (Choose One Idea radio cards)
 *  • sadhana  → Page 8 (Blueprint left + 30-day calendar right + quote)
 *  • siddhi   → Page 9 (Painterly hero left + walnut dashboard right)
 *
 * Every layout shares the global parchment canvas (body) + brass corner
 * flourishes (from <GlobalChrome /> in App.js) + closing walnut ribbon.
 */
import React from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import {
  ArrowLeft, ArrowRight, Check,
} from "lucide-react";
import { GlobalChromeRibbon } from "../components/GlobalChrome";
import Navigation from "../components/homev3/Navigation";
import Footer from "../components/homev3/Footer";
import ParchmentBackground from "../components/ParchmentBackground";
import { StageKicker } from "../components/homev3/parchmentCard";
import { STAGES, STAGE_ORDER, getStage } from "../data/mycraftzStages";
import axios from "axios";
import DemoCraftPrintCard from "../components/mycraftz/DemoCraftPrintCard";
import { MonthPanel, getAgedFixture } from "../components/mycraftz/AgedDemoPreview";
import { useAuth } from "../context/AuthContext";
import "../styles/MyCraftzPage.css";
import "../styles/Parchment.css";
import "../styles/MyCraftzStagePage.css";
import "../styles/MyCraftzStageParchment.css";

/* ───────────────────────────────────────────────────────────
   Painterly heroes — pure SVG, parchment-friendly palette.
   Each one is a small composition matching the mockup's
   illustration brief for that stage.
   ─────────────────────────────────────────────────────────── */

function StageHeroWrap({ children, layout, num, label, dev, sub, slug }) {
  // If a painterly PNG exists for this stage, render it as the hero background.
  const pubUrl = process.env.PUBLIC_URL || "";
  const STAGES_WITH_PNG = ["jigyasa", "bodha", "sankalpa", "sadhana", "siddhi"];
  const hasPng = STAGES_WITH_PNG.includes(slug);
  return (
    <div className={`stg-hero stg-hero-${layout}`} aria-hidden="true">
      {hasPng ? (
        <img
          src={`${pubUrl}/illustrations/stage-${slug}.png`}
          alt=""
          loading="eager"
          className="stg-hero-img"
        />
      ) : (
        <svg viewBox="0 0 1200 480" className="stg-hero-svg" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F1D9A4" />
              <stop offset="100%" stopColor="#B47C3A" />
            </linearGradient>
            <radialGradient id="sun" cx="50%" cy="40%" r="40%">
              <stop offset="0%" stopColor="#F9E7B4" stopOpacity="0.95" />
              <stop offset="60%" stopColor="#F9E7B4" stopOpacity="0.0" />
            </radialGradient>
            <linearGradient id="vignette" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F1D9A4" stopOpacity="0" />
              <stop offset="100%" stopColor="#1A0F0A" stopOpacity="0.55" />
            </linearGradient>
          </defs>
          {children}
          <rect x="0" y="240" width="1200" height="240" fill="url(#vignette)" />
        </svg>
      )}
      <div className="stg-hero-overlay">
        <span className="stg-hero-stage">STAGE {num}</span>
        <h1 className="stg-hero-title">{label}</h1>
        <p className="stg-hero-dev indic">{dev}</p>
        <p className="stg-hero-sub">{sub}</p>
      </div>
    </div>
  );
}

function StageHero({ kind, label, dev, sub, num, layout = "full", slug }) {
  const wrapProps = { layout, num, label, dev, sub, slug };
  switch (kind) {
    case "reader-tree":  // Page 5: reader under illuminated tree at golden hour
      return (
        <StageHeroWrap {...wrapProps}>
          <rect x="0" y="0" width="1200" height="480" fill="url(#sky)" />
          <circle cx="900" cy="180" r="320" fill="url(#sun)" />
          {/* distant hills */}
          <path d="M0 360 L 240 280 L 480 340 L 720 260 L 960 320 L 1200 280 L 1200 480 L 0 480 Z" fill="#8A5A2B" opacity="0.75" />
          <path d="M0 400 L 220 330 L 480 380 L 720 320 L 960 380 L 1200 340 L 1200 480 L 0 480 Z" fill="#5C3818" opacity="0.85" />
          {/* tree silhouette */}
          <g stroke="#3A2510" fill="#3A2510" strokeWidth="2">
            <rect x="345" y="200" width="14" height="180" fill="#3A2510" />
            <ellipse cx="352" cy="170" rx="115" ry="80" fill="#3A2510" />
            <ellipse cx="300" cy="140" rx="75" ry="60" fill="#2C1A12" />
            <ellipse cx="402" cy="140" rx="75" ry="60" fill="#2C1A12" />
          </g>
          {/* reader silhouette */}
          <g fill="#1A0F0A">
            <ellipse cx="380" cy="378" rx="42" ry="6" opacity="0.5" />
            <circle cx="380" cy="328" r="10" />
            <path d="M362 340 Q 380 332 398 340 L 405 380 L 355 380 Z" />
            <rect x="372" y="358" width="20" height="12" fill="#8A5A2B" />
          </g>
        </StageHeroWrap>
      );

    case "open-manuscript":  // Page 6: open illuminated manuscript on a desk
      return (
        <StageHeroWrap {...wrapProps}>
          <rect x="0" y="0" width="1200" height="480" fill="url(#sky)" />
          <radialGradient id="lamp" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F9E7B4" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#F9E7B4" stopOpacity="0" />
          </radialGradient>
          <circle cx="600" cy="180" r="280" fill="url(#lamp)" />
          {/* desk */}
          <rect x="0" y="340" width="1200" height="140" fill="#3A2510" />
          {/* open book pages */}
          <g>
            <path d="M 380 260 L 600 240 L 600 380 L 380 380 Z" fill="#F2DFB7" stroke="#8B6508" strokeWidth="2" />
            <path d="M 600 240 L 820 260 L 820 380 L 600 380 Z" fill="#F2DFB7" stroke="#8B6508" strokeWidth="2" />
            {/* faux lines */}
            <g stroke="#8B6508" strokeWidth="1" opacity="0.4">
              {[270, 285, 300, 315, 330, 345].map((y, i) => (
                <React.Fragment key={i}>
                  <line x1="400" y1={y} x2="580" y2={y - i * 0.5} />
                  <line x1="620" y1={y - i * 0.5} x2="800" y2={y} />
                </React.Fragment>
              ))}
            </g>
            {/* illuminated capital */}
            <rect x="404" y="266" width="34" height="34" fill="#8B3A12" />
            <text x="421" y="294" textAnchor="middle" fontFamily="Playfair Display, serif"
                  fontSize="26" fontWeight="800" fill="#F2DFB7">B</text>
          </g>
          {/* oil lamp */}
          <g fill="#5C3818">
            <rect x="880" y="305" width="60" height="20" />
            <path d="M 890 305 L 905 280 L 930 280 L 940 305 Z" />
            <circle cx="917" cy="270" r="6" fill="#F9E7B4" />
          </g>
        </StageHeroWrap>
      );

    case "parchment-pledge":  // Page 7: pledge parchment beside ink and quill
      return (
        <StageHeroWrap {...wrapProps}>
          <rect x="0" y="0" width="1200" height="480" fill="url(#sky)" />
          <circle cx="600" cy="240" r="280" fill="url(#sun)" />
          {/* unrolled parchment */}
          <path d="M 320 200 Q 600 180 880 200 L 880 380 Q 600 400 320 380 Z" fill="#F9F1DF" stroke="#8B6508" strokeWidth="2" />
          <g stroke="#8B6508" strokeWidth="1" opacity="0.45">
            {[230, 250, 270, 290, 310, 330, 350].map((y) => (
              <line key={y} x1="360" y1={y} x2="840" y2={y} />
            ))}
          </g>
          {/* quill */}
          <g stroke="#3A2510" strokeWidth="3" fill="#3A2510">
            <line x1="900" y1="380" x2="980" y2="180" />
            <path d="M 970 200 Q 990 220 1010 180 Q 990 220 980 240 Q 970 220 970 200 Z" fill="#8B6508" />
          </g>
          <ellipse cx="900" cy="380" rx="30" ry="8" fill="#2C1A12" opacity="0.5" />
        </StageHeroWrap>
      );

    case "blueprint":  // Page 8: blueprint and execution planner
      return (
        <StageHeroWrap {...wrapProps}>
          <rect x="0" y="0" width="1200" height="480" fill="url(#sky)" />
          <circle cx="600" cy="220" r="280" fill="url(#sun)" />
          {/* grid lines suggesting blueprint */}
          <g stroke="#8B6508" strokeWidth="0.8" opacity="0.35">
            {Array.from({ length: 12 }).map((_, i) => (
              <line key={`v${i}`} x1={i * 100} y1="0" x2={i * 100} y2="480" />
            ))}
            {Array.from({ length: 6 }).map((_, i) => (
              <line key={`h${i}`} x1="0" y1={i * 80} x2="1200" y2={i * 80} />
            ))}
          </g>
          {/* compass rose */}
          <g transform="translate(600 260)" stroke="#3A2510" strokeWidth="2" fill="none">
            <circle r="60" />
            <line x1="0" y1="-60" x2="0" y2="60" />
            <line x1="-60" y1="0" x2="60" y2="0" />
            <line x1="-42" y1="-42" x2="42" y2="42" />
            <line x1="-42" y1="42" x2="42" y2="-42" />
            <circle r="6" fill="#8B3A12" />
          </g>
        </StageHeroWrap>
      );

    case "mountain-temple":  // Page 9: mountain temple at sunrise
    default:
      return (
        <StageHeroWrap {...wrapProps}>
          <rect x="0" y="0" width="1200" height="480" fill="url(#sky)" />
          <circle cx="600" cy="190" r="320" fill="url(#sun)" />
          <path d="M0 340 L 200 240 L 360 320 L 560 220 L 760 320 L 960 240 L 1200 320 L 1200 480 L 0 480 Z" fill="#79522B" opacity="0.85" />
          <path d="M0 380 L 180 310 L 340 360 L 540 290 L 760 360 L 960 310 L 1200 360 L 1200 480 L 0 480 Z" fill="#5C3818" opacity="0.9" />
          {/* small temple silhouette on a near peak */}
          <g transform="translate(560 270)" fill="#1A0F0A">
            <path d="M 0 50 L 0 20 L 20 0 L 40 20 L 40 50 Z" />
            <rect x="6" y="50" width="28" height="40" />
            <line x1="20" y1="0" x2="20" y2="-12" stroke="#1A0F0A" strokeWidth="3" />
            <circle cx="20" cy="-14" r="3" />
          </g>
          {/* near mountain */}
          <path d="M0 430 L 220 380 L 420 420 L 640 360 L 880 410 L 1080 380 L 1200 410 L 1200 480 L 0 480 Z" fill="#3A2510" />
        </StageHeroWrap>
      );
  }
}

/* ───────────────────────────────────────────────────────────
   Per-stage layouts
   ─────────────────────────────────────────────────────────── */

function JigyasaLayout({ s }) {
  return (
    <>
      <StageHero kind={s.hero} label={s.title} dev={s.devanagari} sub={s.sub} num={s.num} slug={s.key} />
      <section className="stg-grid stg-grid-two">
        <article className="pt-card stg-book-selected" data-testid="stg-book-selected">
          <span className="pt-eyebrow">BOOK SELECTED</span>
          <div className="stg-book-cover">
            <span className="stg-book-cover-title">{s.selectedBook.title}</span>
          </div>
          <p className="stg-book-author">{s.selectedBook.author}</p>
        </article>
        <article className="pt-card stg-powerful-idea" data-testid="stg-powerful-idea">
          <span className="pt-eyebrow">POWERFUL IDEA</span>
          <h2 className="stg-idea-title">{s.powerfulIdea.label}</h2>
          <blockquote className="stg-idea-quote">&ldquo;{s.powerfulIdea.quote}&rdquo;</blockquote>
        </article>
      </section>
      <section className="pt-card stg-why" data-testid="stg-why">
        <span className="pt-eyebrow">{s.whyMatters.title}</span>
        <p className="stg-why-body">{s.whyMatters.body}</p>
      </section>
    </>
  );
}

function BodhaLayout({ s }) {
  return (
    <>
      <StageHero kind={s.hero} label={s.title} dev={s.devanagari} sub={s.sub} num={s.num} slug={s.key} />
      <section className="pt-card stg-insights" data-testid="stg-insights">
        <span className="pt-eyebrow">{s.insightsTitle}</span>
        <ol className="stg-insights-list">
          {s.insights.map((line, i) => (
            <li key={i} className="stg-insight-row" data-testid={`stg-insight-${i}`}>
              <span className="pt-medallion pt-medallion-sm stg-insight-num">{i + 1}</span>
              <p className="stg-insight-text">{line}</p>
            </li>
          ))}
        </ol>
      </section>
    </>
  );
}

function SankalpaLayout({ s }) {
  return (
    <>
      <header className="stg-center-header">
        <StageKicker stage="sankalpa" text={`STAGE ${s.num} · SANKALPA · ${s.devanagari}`} testId="stg-sankalpa-kicker" />
        <h1 className="stg-center-title">{s.chooseTitle}</h1>
        <p className="pt-tagline">{s.chooseSub}</p>
        <div className="pt-divider"><span /><em>❦</em><span /></div>
      </header>
      <section className="stg-options" data-testid="stg-options">
        {s.options.map((opt) => (
          <label
            key={opt.id}
            className={`pt-card stg-option ${opt.selected ? "is-selected" : ""}`}
            data-testid={`stg-option-${opt.id}`}
          >
            <input type="radio" name="sankalpa-choice" defaultChecked={opt.selected} className="stg-option-input" />
            <span className="stg-option-radio">
              {opt.selected ? <Check size={14} /> : null}
            </span>
            <span className="stg-option-body">
              <span className="stg-option-title">{opt.title}</span>
              <span className="stg-option-desc">{opt.desc}</span>
            </span>
          </label>
        ))}
      </section>
    </>
  );
}

const SAMPLE_BOOK = {
  slug: "atomic-habits", title: "Atomic Habits", author: "James Clear",
  cover: "https://covers.openlibrary.org/b/isbn/9780735211292-M.jpg",
};
const BEGIN_HREF = "/bodha?book=atomic-habits";

function SadhanaLayout({ s }) {
  return (
    <>
      <header className="stg-center-header">
        <StageKicker stage="sadhana" text={`STAGE ${s.num} · SADHANA · साधना`} testId="stg-sadhana-kicker" />
        <h1 className="stg-center-title">{s.title}</h1>
        <p className="pt-tagline" data-testid="stg-sadhana-intro">
          This is a sample practice card. Your own is built from the three ideas you choose.
        </p>
      </header>
      <section className="stg-sample" data-testid="stg-sadhana-sample">
        <DemoCraftPrintCard book={SAMPLE_BOOK} lang="en" />
      </section>
    </>
  );
}

function SiddhiLayout({ s }) {
  return (
    <>
      <header className="stg-center-header">
        <StageKicker stage="siddhi" text={`STAGE ${s.num} · SIDDHI · सिद्धि`} testId="stg-siddhi-kicker" />
        <h1 className="stg-center-title">{s.title}</h1>
        <p className="pt-tagline" data-testid="stg-siddhi-intro">
          Siddhi is not a score. It is what happened when you practised, in your own words.
        </p>
      </header>
      <section className="stg-sample" data-testid="stg-siddhi-sample">
        <div className="mc-active-card dcp-card stg-sample-card">
          <div className="adp-wrap"><MonthPanel fx={getAgedFixture(SAMPLE_BOOK.slug)} slug={SAMPLE_BOOK.slug} /></div>
        </div>
      </section>
    </>
  );
}

/* Begin your own journey (sign in first if needed) + "See my own Siddhi" when one exists. */
function SampleCtas({ stageKey }) {
  const navigate = useNavigate();
  const { isAuthenticated, authHeaders } = useAuth();
  const [ownSiddhi, setOwnSiddhi] = React.useState(null);
  React.useEffect(() => {
    if (stageKey !== "siddhi" || !isAuthenticated) { setOwnSiddhi(null); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install/my-siddhis`, { headers: authHeaders() })
      .then((r) => setOwnSiddhi((r.data?.siddhis || [])[0] || null))
      .catch(() => setOwnSiddhi(null));
  }, [stageKey, isAuthenticated, authHeaders]);
  const begin = () => (isAuthenticated ? navigate(BEGIN_HREF) : navigate("/login", { state: { from: BEGIN_HREF } }));
  return (
    <>
      <button type="button" className="pt-btn-rust" onClick={begin} data-testid="stg-begin-journey">
        Begin your own journey <ArrowRight size={14} />
      </button>
      {ownSiddhi && (
        <Link to={`/siddhi?book=${ownSiddhi.book_id}`} className="stg-own-siddhi" data-testid="stg-see-own-siddhi">
          See my own Siddhi
        </Link>
      )}
    </>
  );
}

/* ───────────────────────────────────────────────────────────
   Stage page entry
   ─────────────────────────────────────────────────────────── */
export default function MyCraftzStagePage() {
  const { stage } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("from") === "preview";
  const s = getStage(stage);
  const idx = STAGE_ORDER.indexOf(s.key);
  const prevKey = idx > 0 ? STAGE_ORDER[idx - 1] : null;
  const nextKey = s.next;

  const LayoutByKey = {
    jigyasa: JigyasaLayout,
    bodha: BodhaLayout,
    sankalpa: SankalpaLayout,
    sadhana: SadhanaLayout,
    siddhi: SiddhiLayout,
  };
  const Layout = LayoutByKey[s.key] || JigyasaLayout;

  // Back-button routing:
  //  - `?from=preview` → homepage (escape hatch, unchanged)
  //  - Sadhana / Siddhi (System-B stage pages) → previous stage's REAL
  //    System-B page (/sankalpa, /sadhana), NOT the frozen mockup at
  //    /mycraftz/stage/<prev>. This matches what the visitor actually
  //    experienced walking through the journey.
  //  - Other stages → previous stage's mockup (existing behaviour).
  const REAL_STAGE_PATH = { bodha: "/bodha", sankalpa: "/sankalpa", sadhana: "/sadhana" };
  const useRealBack = s.key === "sadhana" || s.key === "siddhi";
  const backHref = isPreview
    ? "/"
    : useRealBack && prevKey && REAL_STAGE_PATH[prevKey]
      ? REAL_STAGE_PATH[prevKey]
      : prevKey
        ? `/mycraftz/stage/${prevKey}`
        : "/mycraftz/intro";

  // For Sadhana & Siddhi we wrap the whole page in the shared homev3
  // Navigation + Footer chrome and apply the parchment-card retrofit.
  // Other stages keep their existing chrome (unchanged this session).
  const useParchmentShell = s.key === "sadhana" || s.key === "siddhi";
  const reduceMotion = useReducedMotion();

  const pageBody = (
    <main
      className="parchment-theme stg-page"
      data-testid={`stg-page-${s.key}`}
      style={{ background: "transparent" }}
    >
      <ParchmentBackground />
      {/* Top nav with stage stepper */}
      <div className="stg-nav">
        <button
          type="button"
          className="pt-btn-walnut"
          onClick={() => navigate(backHref)}
          data-testid="stg-back"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <ol className="stg-stepper" aria-label="MyCraftz stages">
          {STAGE_ORDER.map((k, i) => {
            const isCurrent = k === s.key;
            const isPast = i < idx;
            return (
              <li
                key={k}
                className={`stg-step ${isCurrent ? "is-current" : ""} ${isPast ? "is-past" : ""}`}
                data-testid={`stg-stepper-${k}`}
              >
                {isPreview ? (
                  <span className="stg-step-link" aria-disabled="true">
                    <span className="pt-medallion pt-medallion-sm stg-step-medallion">{i + 1}</span>
                    <span className="stg-step-name">{STAGES[k].title}</span>
                  </span>
                ) : (
                  <Link to={`/mycraftz/stage/${k}`} className="stg-step-link">
                    <span className="pt-medallion pt-medallion-sm stg-step-medallion">{i + 1}</span>
                    <span className="stg-step-name">{STAGES[k].title}</span>
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="stg-body"
        key={s.key}
      >
        <Layout s={s} />
      </motion.div>

      {/* Footer CTAs */}
      <section className="stg-footer-ctas">
        {useParchmentShell ? (
          <SampleCtas stageKey={s.key} />
        ) : nextKey ? (
          <button
            type="button"
            className="pt-btn-rust"
            onClick={() => navigate(`/mycraftz/stage/${nextKey}${isPreview ? "?from=preview" : ""}`)}
            data-testid="stg-next"
          >
            {s.nextCta} <ArrowRight size={14} />
          </button>
        ) : null}
      </section>

      {!useParchmentShell && <GlobalChromeRibbon />}
    </main>
  );

  if (useParchmentShell) {
    // Wrapper: shared Navigation → parchment shell → page body → shared Footer.
    // `stg-parchment-shell` class scopes the CSS overrides in MyCraftzStageParchment.css.
    const activePath = `/mycraftz/stage/${s.key}`;
    return (
      <div data-testid={`stg-shell-${s.key}`} className="stg-parchment-shell">
        <Navigation activePath={activePath} />
        {pageBody}
        <Footer activePath={activePath} />
      </div>
    );
  }

  return pageBody;
}
